import { useGetUsersByTeamIdQuery } from '../../api/client'
import SimpleLoader from '../SimpleLoader'
import { useRecoilState } from 'recoil'
import { isUserAdminState } from '../../store/DomainStore'

type Props = {
    teamId: number
}

// Two-letter monogram from an email address: initials of the first two
// dot/underscore/plus-separated words in the local part, else its first
// two characters. Matches the monogram device used on the admin lists.
function monogram(username: string): string {
    const localPart = username.split('@')[0] ?? username
    const words = localPart.split(/[.\-_+]+/).filter(Boolean)
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase()
    return localPart.slice(0, 2).toUpperCase()
}

export default function TeamUsersDisplay(props: Props) {
    const { teamId } = props
    const { data, loading } = useGetUsersByTeamIdQuery({
        variables: {
            teamId: teamId
        },
        onError: (error) => {
            throw Error(error.message)
        }
    })

    const [admin] = useRecoilState(isUserAdminState)

    const users = data?.getUsersByTeamId || []

    if (loading) {
        return <SimpleLoader loading={loading} />
    }

    return (
        <div>
            <p className="mb-1 text-sm font-medium text-ink-700">
                {users.length} member{users.length === 1 ? '' : 's'}
            </p>
            <ul className="divide-y divide-ink-100">
                {users.map((user) => {
                    const avatar = (
                        <span
                            className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-ink-100 text-xs font-semibold text-ink-600"
                            aria-hidden="true"
                        >
                            {monogram(user.username)}
                        </span>
                    )

                    if (admin) {
                        return (
                            <li key={user.id}>
                                <a
                                    href={`#/admin/user/edit/${user.id}`}
                                    className="group -mx-2 flex items-center gap-3 rounded-md px-2 py-2.5 transition-colors hover:bg-ink-50"
                                >
                                    {avatar}
                                    <span className="truncate text-sm font-medium text-ink-800 group-hover:text-arwen-600">
                                        {user.username}
                                    </span>
                                </a>
                            </li>
                        )
                    }

                    return (
                        <li key={user.id} className="flex items-center gap-3 py-2.5">
                            {avatar}
                            <span className="truncate text-sm text-ink-800">{user.username}</span>
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}
